import React from 'react';
import { useTranslation } from 'react-i18next';
import { GitBranch, ExternalLink } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import { commitWebUrl } from '../../services/git/commitWebUrl';
import { hostOf } from '../../graph/repoRefs';
import { openExternal } from '../../utils/openExternal';
import { copyTextToClipboard } from '../../utils/clipboard';
import { useNotificationStore } from '../../store/notificationStore';
import { whenLabel } from './GitHistory';
import '../boards/TaskCommits.css';

/**
 * One commit of a code repository (commit ↔ task links): short sha (click
 * copies the full id), summary, author, date, repository, branch, how it is
 * linked, the provider link — and `actions` (Unlink, Link to task…) and
 * `children` (task chips) from the caller. Used by the task window's Commits
 * section and the project's Code section.
 */
export default function CodeCommitRow({ commit, actions = null, children = null, showRepo = true }) {
  const { t } = useTranslation();
  const url = commitWebUrl(commit.remoteUrl, commit.oid, commit.repo?.provider);
  const host = url ? hostOf(commit.remoteUrl) : '';
  const short = String(commit.oid || '').slice(0, 7);
  const copySha = async () => {
    const ok = await copyTextToClipboard(commit.oid);
    if (ok) useNotificationStore.getState().success(commit.oid, t('taskLinks.commits.shaCopied'));
  };
  return (
    <li className="task-commit" data-testid="task-commit" data-sha={commit.oid}>
      <Tooltip content={t('taskLinks.commits.copySha')} side="top">
        <button type="button" className="task-commit-sha" onClick={copySha}>
          {short}
        </button>
      </Tooltip>
      <div className="task-commit-main">
        <span className="task-commit-summary">{commit.summary}</span>
        <span className="task-commit-meta">
          {commit.author && <span>{commit.author}</span>}
          {commit.time ? <span>{whenLabel(commit.time)}</span> : null}
          {showRepo && commit.repo?.name && <span className="task-commit-repo">{commit.repo.name}</span>}
          {commit.branch && (
            <span className={`task-commit-branch${commit.onDefault ? ' task-commit-branch--default' : ''}`}>
              <GitBranch size={11} aria-hidden />
              {commit.branch}
            </span>
          )}
          {commit.linkedBy === 'branch' && <span className="task-commit-via">{t('taskLinks.commits.viaBranch')}</span>}
          {commit.manual && <span className="task-commit-via">{t('taskLinks.commits.viaManual')}</span>}
          {commit.closedTask && <span className="task-commit-closed">{t('taskLinks.commits.closedTask')}</span>}
          {children}
        </span>
      </div>
      {actions}
      {url && (
        <Tooltip content={t('taskLinks.commits.openOnProvider', { host })} side="top">
          <button type="button" className="task-commit-open" onClick={() => openExternal(url)}>
            <ExternalLink size={13} aria-hidden />
          </button>
        </Tooltip>
      )}
    </li>
  );
}
